import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSyncEvents } from '../api';
import { formatTimestamp, formatRelativeTime } from '../utils';
import { useFilters } from '../hooks';
import type { SyncEvent, SyncEventFilters, PaginatedResponse, NameDeduplicationDetail } from '../types';
import styles from './Deduplication.module.css';

/**
 * Deduplication Page
 * 
 * Shows sync events where files were renamed with _1, _2 suffixes.
 * Highlights mass deduplication events as alerts (potential sync bugs).
 */

export default function Deduplication() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<SyncEvent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  
  const [search, setSearch] = useState('');
  const [showMassOnly, setShowMassOnly] = useState(false);
  const { timeRange, setTimeRange } = useFilters();
  const [page, setPage] = useState(1);
  const pageSize = 30;

  const fetchEvents = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const filters: SyncEventFilters = {
        page,
        page_size: pageSize,
        ordering: '-timestamp',
        has_name_deduplication: true,
      };

      if (showMassOnly) {
        filters.is_mass_deduplication = true;
      }

      if (search.trim()) {
        if (search.includes('@')) {
          filters.user_email = search.trim();
        } else {
          filters.device_code = search.trim();
        }
      }

      if (timeRange) {
        const now = new Date();
        let since: Date;
        switch (timeRange) {
          case '1h':
            since = new Date(now.getTime() - 60 * 60 * 1000);
            break;
          case '24h':
            since = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            break;
          case '7d':
            since = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30d':
            since = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          default:
            since = new Date(0);
        }
        filters.timestamp_gte = since.toISOString();
      }

      console.log('[Deduplication] Fetching with filters:', filters);
      const response: PaginatedResponse<SyncEvent> = await getSyncEvents(filters);
      
      console.log(`[Deduplication] API returned ${response.results?.length || 0} results, count: ${response.count}`);
      
      // Debug: log first event to see actual field values
      if (response.results?.length > 0) {
        const sample = response.results[0];
        console.log('[Deduplication] Sample event fields:', {
          has_name_deduplication: sample.has_name_deduplication,
          name_deduplication_count: sample.name_deduplication_count,
          name_deduplication_total_files: sample.name_deduplication_total_files,
          is_mass_deduplication: sample.is_mass_deduplication,
          event_type: sample.event_type,
        });
      }
      
      // Check if any events in the response have deduplication data
      const eventsWithDedup = response.results?.filter(e => 
        e.has_name_deduplication === true || 
        (e.name_deduplication_count && e.name_deduplication_count > 0)
      ) || [];
      
      console.log(`[Deduplication] Events with deduplication data: ${eventsWithDedup.length}`);
      
      // Filter client-side as defensive measure in case backend doesn't filter properly
      let filteredEvents = response.results?.filter(e => e.has_name_deduplication === true) || [];
      
      // Apply mass deduplication filter client-side as well
      if (showMassOnly) {
        filteredEvents = filteredEvents.filter(e => e.is_mass_deduplication === true);
      }
      
      // Log warning if backend returned non-deduplication events
      if (response.results && response.results.length > 0 && filteredEvents.length !== response.results.length) {
        console.warn(
          `[Deduplication] Backend returned ${response.results.length} events but only ${filteredEvents.length} have deduplication. ` +
          `Backend may not be filtering by has_name_deduplication correctly.`
        );
      }
      
      // If no filtered events but we have dedup data, show those anyway
      if (filteredEvents.length === 0 && eventsWithDedup.length > 0) {
        console.log('[Deduplication] Using events with dedup data since has_name_deduplication filter returned nothing');
        filteredEvents = eventsWithDedup;
      }
      
      setEvents(filteredEvents);
      setTotalCount(filteredEvents.length);
    } catch (err) {
      console.error('[Deduplication] Error fetching:', err);
      setError('Failed to load deduplication events');
    } finally {
      setIsLoading(false);
    }
  }, [search, showMassOnly, timeRange, page]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    setPage(1);
  }, [search, showMassOnly, timeRange]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const getDeduplicationPercentage = (event: SyncEvent): number => {
    if (!event.name_deduplication_count || !event.name_deduplication_total_files) return 0;
    return Math.round((event.name_deduplication_count / event.name_deduplication_total_files) * 100);
  };

  const renderDeduplicationDetails = (details: NameDeduplicationDetail[] | null) => {
    if (!details || details.length === 0) {
      return <p className={styles.noDetails}>No deduplication details available.</p>;
    }

    return (
      <div className={styles.detailsTable}>
        <div className={styles.detailsHeader}>
          <span>Original Name</span>
          <span>New Name</span>
          <span>File ID</span>
        </div>
        {details.map((detail, idx) => (
          <div key={idx} className={styles.detailsRow}>
            <span className={styles.originalName}>{detail.original_name}</span>
            <span className={styles.deduplicatedName}>{detail.deduplicated_name}</span>
            <span className={styles.fileId} title={detail.file_id}>
              {detail.file_id.slice(0, 8)}...
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Count mass deduplication alerts
  const massDeduplicationCount = events.filter(e => e.is_mass_deduplication).length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <span className={styles.titleIcon}>🔀</span>
            Name Deduplication
          </h1>
          <p className={styles.subtitle}>
            {totalCount.toLocaleString()} events with file renaming ({showMassOnly ? 'mass only' : 'all'})
          </p>
        </div>
      </div>

      {/* Mass Deduplication Alert Banner */}
      {massDeduplicationCount > 0 && !showMassOnly && (
        <div className={styles.alertBanner}>
          <div className={styles.alertIcon}>🚨</div>
          <div className={styles.alertContent}>
            <strong>Mass Deduplication Detected</strong>
            <p>
              {massDeduplicationCount} event{massDeduplicationCount !== 1 ? 's' : ''} with unusually high deduplication rates.
              This may indicate a sync bug that needs investigation.
            </p>
          </div>
          <button 
            className={styles.alertButton}
            onClick={() => setShowMassOnly(true)}
          >
            Show Alerts Only
          </button>
        </div>
      )}

      <div className={styles.filters}>
        <input
          type="text"
          placeholder="Search by user email or device code..."
          className={styles.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className={styles.filterGroup}>
          <select 
            className={styles.select}
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="">All Time</option>
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <label className={styles.checkbox}>
            <input 
              type="checkbox" 
              checked={showMassOnly}
              onChange={(e) => setShowMassOnly(e.target.checked)}
            />
            <span>🚨 Mass Deduplication Only</span>
          </label>
        </div>
      </div>

      {error && (
        <div className={styles.error}>{error}</div>
      )}

      <div className={styles.tableContainer}>
        {isLoading ? (
          <div className={styles.loading}>Loading deduplication events...</div>
        ) : (
          <>
            {events.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>✅</div>
                <h3>No Deduplication Events</h3>
                <p>No files have been renamed with _1, _2 suffixes matching your filters.</p>
              </div>
            ) : (
              <div className={styles.eventList}>
                {events.map((event) => {
                  const percentage = getDeduplicationPercentage(event);
                  const isExpanded = expandedEventId === event.id;
                  
                  return (
                    <div 
                      key={event.id} 
                      className={`${styles.eventCard} ${event.is_mass_deduplication ? styles.massDeduplication : ''}`}
                    >
                      <div 
                        className={styles.eventHeader}
                        onClick={() => setExpandedEventId(isExpanded ? null : event.id)}
                      >
                        <div className={styles.eventMain}>
                          {event.is_mass_deduplication && (
                            <span className={styles.alertTag}>🚨 MASS</span>
                          )}
                          <div className={styles.eventStats}>
                            <span className={styles.statBig}>
                              {event.name_deduplication_count ?? 0}
                            </span>
                            <span className={styles.statLabel}>
                              of {event.name_deduplication_total_files ?? '?'} files renamed
                            </span>
                            <span className={styles.percentageBadge} data-severity={percentage > 50 ? 'high' : percentage > 30 ? 'medium' : 'low'}>
                              {percentage}%
                            </span>
                          </div>
                        </div>
                        
                        <div className={styles.eventMeta}>
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>User</span>
                            <span className={styles.metaValue}>{event.user_email || '-'}</span>
                          </div>
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Device</span>
                            <span className={styles.metaValueMono}>{event.device_code || '-'}</span>
                          </div>
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Time</span>
                            <span className={styles.metaValue} title={formatTimestamp(event.timestamp)}>
                              {formatRelativeTime(event.timestamp)}
                            </span>
                          </div>
                        </div>

                        <div className={styles.eventActions}>
                          <button 
                            className={styles.expandButton}
                            title={isExpanded ? 'Collapse' : 'Expand details'}
                          >
                            {isExpanded ? '▲' : '▼'}
                          </button>
                          <button 
                            className={styles.viewButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/events/${event.id}`);
                            }}
                            title="View full event details"
                          >
                            View →
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className={styles.eventDetails}>
                          <div className={styles.detailsSection}>
                            <h4>Renamed Files</h4>
                            {renderDeduplicationDetails(event.name_deduplication_details)}
                          </div>
                          <div className={styles.eventInfo}>
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Session ID</span>
                              <span className={styles.infoValueMono}>{event.session_id?.slice(0, 12)}...</span>
                            </div>
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Timestamp</span>
                              <span className={styles.infoValue}>{formatTimestamp(event.timestamp)}</span>
                            </div>
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Device Name</span>
                              <span className={styles.infoValue}>{event.device_name || '-'}</span>
                            </div>
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Firmware</span>
                              <span className={styles.infoValue}>{event.firmware_version || '-'}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageButton}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ← Previous
          </button>
          <span className={styles.pageInfo}>
            Page {page} of {totalPages}
          </span>
          <button
            className={styles.pageButton}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

